// Recent console output and uncaught errors from this tab, attached to feedback so a report shows
// what the browser saw. Kept in memory only and capped, and secrets are masked before storing.

const MAX_ENTRIES = 200;
const MAX_MESSAGE_LENGTH = 2000;
const LEVELS = ["error", "warn", "info", "log", "debug"] as const;

interface BrowserLogEntry {
  time: number;
  level: string;
  message: string;
}

const entries: BrowserLogEntry[] = [];
let installed = false;

// API keys (`clsk_…`, and `sk_…` from before that format) and bearer or session tokens (JWTs) must never leave the browser in a report.
const redact = (text: string) =>
  text
    .replace(/\b(clsk|sk)_[A-Za-z0-9_-]{8,}/g, "$1_[redacted]")
    .replace(/\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g, "[redacted token]");

const describe = (value: unknown): string => {
  if (value instanceof Error) {
    const message = `${value.name}: ${value.message}`;
    return value.stack?.includes(message) ? value.stack : `${message}\n${value.stack ?? ""}`;
  }
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value) ?? String(value);
  } catch {
    return String(value);
  }
};

const record = (level: string, values: unknown[]) => {
  const message = redact(values.map(describe).join(" ")).slice(0, MAX_MESSAGE_LENGTH);
  entries.push({ time: Date.now(), level, message });
  if (entries.length > MAX_ENTRIES) entries.splice(0, entries.length - MAX_ENTRIES);
};

/** Starts keeping recent console output and uncaught errors. Safe to call more than once. */
export const installBrowserLogCapture = () => {
  if (installed || typeof window === "undefined") return;
  installed = true;
  for (const level of LEVELS) {
    // biome-ignore lint/suspicious/noConsole: wraps the console to record what it prints.
    const original = console[level].bind(console);
    console[level] = (...values: unknown[]) => {
      record(level, values);
      original(...values);
    };
  }
  window.addEventListener("error", (event) => record("uncaught", [event.error ?? event.message]));
  window.addEventListener("unhandledrejection", (event) =>
    record("unhandled rejection", [event.reason]),
  );
};

/** The captured entries as plain text, oldest first, or `undefined` when there are none. */
export const getBrowserLogs = (): string | undefined => {
  if (!entries.length) return undefined;
  const header = `${navigator.userAgent}\n${window.innerWidth}x${window.innerHeight} ${window.location.pathname}\n\n`;
  return (
    header +
    entries
      .map((entry) => `${new Date(entry.time).toISOString()} [${entry.level}] ${entry.message}`)
      .join("\n")
  );
};
